import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';

export const CLIENT_STATE_ORDER = ['affiliated', 'contacted', 'visited', 'completed'];
export const FINAL_CLIENT_STATE = CLIENT_STATE_ORDER[CLIENT_STATE_ORDER.length - 1];

const SPRING_SNAP = { type: 'spring', stiffness: 420, damping: 28 };
const SPRING_SOFT = { type: 'spring', stiffness: 320, damping: 26 };
const ENTER_EASE = { ease: [0.23, 1, 0.32, 1], duration: 0.35 };

export function isClientAtFinalStage(state) {
  return CLIENT_STATE_ORDER.indexOf(state || 'affiliated') === CLIENT_STATE_ORDER.length - 1;
}

function getStateTimelineStep(currentState, idx) {
  const currentStateIndex = CLIENT_STATE_ORDER.indexOf(currentState || 'affiliated');
  return {
    currentStateIndex,
    isCompleted: idx < currentStateIndex,
    isCurrent: idx === currentStateIndex,
    isNext: idx === currentStateIndex + 1,
  };
}

function FinalStageCelebration({ getStateLabel, isCompact, prefersReducedMotion }) {
  const FinalStateIcon = getStateLabel(FINAL_CLIENT_STATE).icon;
  const circleClass = isCompact ? 'h-8 w-8' : 'h-12 w-12';
  const iconClass = isCompact ? 'h-4 w-4' : 'h-6 w-6';

  return (
    <motion.div
      key="final-stage"
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.4, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85, y: -8 }}
      transition={prefersReducedMotion ? { duration: 0.15 } : SPRING_SOFT}
      className="flex justify-center py-1"
    >
      <div className="relative flex flex-col items-center">
        {!prefersReducedMotion ? (
          <>
            <motion.span
              aria-hidden="true"
              className={`absolute rounded-full bg-success-400/30 ${circleClass}`}
              initial={{ scale: 0.8, opacity: 0.7 }}
              animate={{ scale: [1, 1.55, 1.35], opacity: [0.55, 0, 0.35] }}
              transition={{ duration: 1.1, ease: ENTER_EASE.ease }}
            />
            <motion.span
              aria-hidden="true"
              className={`absolute rounded-full ring-2 ring-success-300/70 ${circleClass}`}
              animate={{ scale: [1, 1.2, 1], opacity: [0.85, 0.25, 0.85] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
          </>
        ) : null}
        <motion.div
          className={`relative flex items-center justify-center rounded-full bg-success-500 text-white shadow-glow ring-2 ring-success-200 ${circleClass}`}
          initial={prefersReducedMotion ? false : { rotate: -18, scale: 0.6 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={SPRING_SNAP}
        >
          <FinalStateIcon className={iconClass} />
        </motion.div>
        {!isCompact ? (
          <motion.span
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...ENTER_EASE, delay: prefersReducedMotion ? 0 : 0.12 }}
            className="mt-2 text-center text-xs font-semibold text-success-600"
          >
            {getStateLabel(FINAL_CLIENT_STATE).label}
          </motion.span>
        ) : null}
      </div>
    </motion.div>
  );
}

function TimelineStepCircle({
  state,
  idx,
  currentState,
  advanceTick,
  getStateLabel,
  isCompact,
  prefersReducedMotion,
}) {
  const { isCompleted, isCurrent, isNext } = getStateTimelineStep(currentState, idx);
  const StateIcon = getStateLabel(state).icon;
  const circleClass = isCompact ? 'h-8 w-8' : 'h-12 w-12';
  const iconClass = isCompact ? 'h-4 w-4' : 'h-6 w-6';

  let toneClass =
    'bg-secondary-200 text-secondary-500  ';
  if (isCompleted) {
    toneClass = 'bg-success-500 text-white ring-2 ring-success-200';
  } else if (isCurrent) {
    toneClass = 'bg-orange-500 text-white ring-4 ring-orange-200';
  } else if (isNext) {
    toneClass = 'bg-orange-100 text-orange-600 ring-2 ring-orange-300';
  }

  return (
    <motion.div
      layout
      initial={false}
      animate={
        prefersReducedMotion
          ? { scale: isCurrent ? 1.05 : 1, opacity: 1 }
          : {
              scale: isCurrent ? 1.12 : isCompleted ? 1 : isNext ? 1.04 : 1,
              opacity: 1,
            }
      }
      transition={isCurrent || isCompleted ? SPRING_SNAP : { duration: 0.25, ease: 'easeInOut' }}
      className={`relative flex items-center justify-center rounded-full ${circleClass} ${toneClass}`}
    >
      {!prefersReducedMotion && isNext ? (
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-full ring-2 ring-orange-300/80"
          animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0, 0.55] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      ) : null}
      <AnimatePresence mode="wait" initial={false}>
        {isCompleted ? (
          <motion.span
            key={`${state}-check-${advanceTick}`}
            initial={prefersReducedMotion ? false : { scale: 0, opacity: 0, rotate: -40 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={SPRING_SNAP}
            className="relative flex items-center justify-center"
          >
            <CheckIcon className={iconClass} />
          </motion.span>
        ) : (
          <motion.span
            key={`${state}-icon-${advanceTick}`}
            initial={prefersReducedMotion ? false : { scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={ENTER_EASE}
            className="relative flex items-center justify-center"
          >
            <StateIcon className={iconClass} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function TimelineArrowConnector({
  idx,
  currentStateIndex,
  advanceTick,
  isCompact,
  prefersReducedMotion,
}) {
  const isFilled = idx < currentStateIndex;
  const isActive = idx === currentStateIndex;
  const isPending = !isFilled && !isActive;
  const svgHeight = isCompact ? 8 : 10;
  const lineEnd = 84;
  const clipId = `timeline-arrow-clip-${idx}-${advanceTick}-${currentStateIndex}`;

  const trackColor = '#cbd5e1';
  const fillColor = isFilled ? '#22c55e' : isActive ? '#f97316' : 'transparent';
  const fillTarget = isFilled ? lineEnd : isActive ? 100 : 0;

  return (
    <div
      className="flex min-w-0 flex-1 items-center"
      style={{ width: isCompact ? 'calc(100% / 3 - 60px)' : 'calc(100% / 3 - 80px)' }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 100 8"
        preserveAspectRatio="none"
        className="w-full overflow-visible"
        style={{ height: svgHeight }}
      >
        <defs>
          <clipPath id={clipId}>
            <motion.rect
              x="0"
              y="0"
              height="8"
              initial={prefersReducedMotion ? false : { width: 0 }}
              animate={{ width: fillTarget }}
              transition={isFilled || isActive ? SPRING_SNAP : ENTER_EASE}
            />
          </clipPath>
        </defs>

        {!isPending ? (
          isActive ? (
            <path
              d={`M 1 3.25 H ${lineEnd} V 1.5 L 98 4 L ${lineEnd} 6.5 V 4.75 H 1 Z`}
              fill="#e2e8f0"
            />
          ) : (
            <line
              x1="1"
              y1="4"
              x2={String(lineEnd)}
              y2="4"
              stroke={trackColor}
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          )
        ) : (
          <line
            x1="1"
            y1="4"
            x2={String(lineEnd)}
            y2="4"
            stroke={trackColor}
            strokeWidth="1.5"
            strokeOpacity="0.45"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {(isFilled || isActive) && fillTarget > 0 ? (
          <g clipPath={`url(#${clipId})`}>
            {isActive ? (
              <path
                d={`M 1 3.25 H ${lineEnd} V 1.5 L 98 4 L ${lineEnd} 6.5 V 4.75 H 1 Z`}
                fill="#f97316"
              />
            ) : (
              <line
                x1="1"
                y1="4"
                x2={String(lineEnd)}
                y2="4"
                stroke={fillColor}
                strokeWidth="1.5"
                strokeLinecap="butt"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </g>
        ) : null}
      </svg>
    </div>
  );
}

function ClientStateTimeline({
  currentState,
  variant = 'compact',
  getStateLabel,
  advanceTick = 0,
}) {
  const prefersReducedMotion = useReducedMotion();
  const isFinal = isClientAtFinalStage(currentState);
  const isCompact = variant === 'compact';

  return (
    <AnimatePresence mode="wait">
      {isFinal ? (
        <FinalStageCelebration
          getStateLabel={getStateLabel}
          isCompact={isCompact}
          prefersReducedMotion={prefersReducedMotion}
        />
      ) : (
        <motion.div
          key={`timeline-${advanceTick}-${currentState}`}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, scale: 0.94, y: -6, filter: 'blur(3px)' }
          }
          transition={ENTER_EASE}
          className={`flex items-center ${isCompact ? 'mb-2' : ''}`}
        >
          {CLIENT_STATE_ORDER.map((state, idx) => {
            const { currentStateIndex, isCompleted, isCurrent, isNext } = getStateTimelineStep(
              currentState,
              idx
            );

            return (
              <React.Fragment key={state}>
                <div
                  className="flex flex-col items-center"
                  style={{ width: isCompact ? '60px' : '80px' }}
                >
                  <TimelineStepCircle
                    state={state}
                    idx={idx}
                    currentState={currentState}
                    advanceTick={advanceTick}
                    getStateLabel={getStateLabel}
                    isCompact={isCompact}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                  {!isCompact ? (
                    <motion.span
                      layout
                      animate={{
                        color: isCurrent
                          ? '#ea580c'
                          : isNext
                            ? '#f97316'
                            : isCompleted
                              ? '#16a34a'
                              : '#94a3b8',
                      }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="mt-2 text-center text-xs font-semibold"
                    >
                      {getStateLabel(state).label}
                    </motion.span>
                  ) : null}
                </div>
                {idx < CLIENT_STATE_ORDER.length - 1 ? (
                  <TimelineArrowConnector
                    idx={idx}
                    currentStateIndex={currentStateIndex}
                    advanceTick={advanceTick}
                    isCompact={isCompact}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ClientStateTimeline;
